import React from 'react';
import Layout from './Layout';
import { motion } from 'framer-motion';

const AAticleBusinessPartnering = () => {
  const sections = [
    {
      id: 1,
      title: "Business Partnering: The Delta Between Finance and Operations",
      text: `Business partnering is a term that is gaining traction in the corporate world, especially in the realm of finance. At its core, business partnering is about creating a strong, collaborative relationship between the finance team and other departments within an organization. But what does it take to to do business partnering well? And why is it important for modern businesses? Let us break it down.`,
      explanation: "Understanding the evolution and importance of business partnering in modern organizations"
    },
    {
      id: 2,
      title: "Defining Business Partnering",
      text: `Business partnering is when finance professionals go beyond number-crunching and actively engage with other teams to drive business decisions. Instead of just reporting the financial data, finance teams interpret it, explain its implications, and work closely with operations, marketing, or sales to achieve the organization's goals.`,
      explanation: "Defining the core concept of business partnering"
    },
    {
      id: 3,
      title: "Why Business Partnering?",
      text: `The traditional role of finance was often limited to budgeting, forecasting, and ensuring compliance. While these tasks remain critical, they don't always contribute directly to a company's growth or innovation. Business partnering fills this gap by fostering collaboration between finance and other departments—like gears working together in a well-oiled machine. Think about it using common analogies:

Better Decision-Making: Imagine a GPS helping a driver navigate. Finance professionals guide teams by showing the financial implications of their choices, helping them stay on course to achieve the company budget goals.

Alignment of Goals: Think of a rowing team. When everyone rows in sync, the boat moves efficiently. By working together, all departments ensure their strategies align with the company's overall objectives which improves the internal culture.

Proactive Problem-Solving: Consider a gardener spotting weeds early in a flower bed. A collaborative approach allows teams to identify potential issues before they grow out of control. This allows the organisation to find better solutions and steer clear of operational risks as there is a second opinion.

Increased Efficiency: Picture a chef in a busy kitchen. With a clear plan and precise resource allocation, the chef can focus on creating delicious meals. Similarly, finance's guidance helps teams concentrate on what truly drives results. Less is more.`,
      explanation: "Understanding the strategic importance of well executed business partnering"
    },
    
    {
      id: 4,
      title: "Key Skills for Effective Business Partnering",
      text: `Being a great business partner, requires more than just technical financial knowledge. Like assembling a puzzle, each skill fits together to create a complete picture of effective collaboration. Here are some essential skills you should consider to develop:

Communication: Understanding complex financial data and translating it into simple terms that non-financial colleagues can easily grasp.

Empathy: Empathy allows the business partner to understand the unique challenges, priorities, and goals of other departments.

Problem-Solving: A good business partner does not stop at identifying problems. They dig deeper to find actionable solutions.

Strategic Thinking: The business partner must see the big picture, anticipating future challenges and opportunities.

Adaptability: Everyone is different, so the skilled business partner is able to tailor-make their service.`,
      explanation: "Essential skills needed for successful business partnering"
    },
    
  ];

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      <Layout />
      <div className="w-full min-h-screen bg-gradient-to-b from-white to-white text-black pt-20">
        <div className="max-w-screen-lg mx-auto p-4">
          {/* Add Table of Contents */}
          <div className="mb-12 flex justify-center">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm max-w-[600px] w-full">
              <h2 className="text-2xl font-bold mb-4 text-center">Table of Contents</h2>
              <div className="space-y-2">
                {sections.map(section => section.title && (
                  <div 
                    key={section.id} 
                    className="cursor-pointer hover:scale-110 transition-colors text-center"
                    onClick={() => scrollToSection(`section-${section.id}`)}
                  >
                    {section.id}. {section.title}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-16">
            {sections.map(({ id, title, text, explanation }, index) => (
              <motion.div 
                key={id}
                id={`section-${id}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: index * 0.3 }}
                className="flex flex-col gap-6"
              >
                <h2 className="text-3xl font-bold">{title}</h2>
                <div className="text-lg whitespace-pre-wrap">{text}</div>
                {explanation && (
                  <p className="text-gray-600 italic">{explanation}</p>
                )}
              </motion.div>
            ))}
          </div>

          {/* Add Back to Top Button */}
          <div className="flex justify-center mt-12 mb-8">
            <button
              onClick={scrollToTop}
              className="text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors duration-300 hover:scale-110"
              style={{ background: 'rgb(1, 50, 32)' }}
            >
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AAticleBusinessPartnering;